// The engine's own children share their parent's transcript stream. They
// have no Poulpe session or page, and their questions stay with the parent.
import { useEffect, useRef, useState } from 'react'
import { errorMessage } from '../api/client'
import type { Me, Runner, Session } from '../api/types'
import { uuid4 } from '../api/uuid'
import { Working } from '../components/Working'
import { toast } from '../components/toasts'
import type { TranscriptView } from '../sessions/follow'
import { useStore } from '../store/context'
import type { EngineAgent } from '../transcript/acp'
import { Transcript } from '../transcript/Transcript'
import { cx } from '../transcript/bits'
import { concrete } from './choices'
import { agentLabel } from './format'
import { SubAgentSettings, engineSettings } from './SubAgentSettings'

/** An engine child as the pane lists it; a nested one carries its parent child's effort, which it runs at unless it sets its own. */
export type ShownEngineAgent = EngineAgent & {readonly leadEffort?: string}

export function engineAgentsOf(agents: readonly EngineAgent[], leadEffort?: string): ShownEngineAgent[] {
  const launches = new Set(agents.flatMap(agent => agent.launches ?? []))
  return agents.filter(agent => !agent.legacy || !launches.has(agent.id.slice('tool:'.length))).flatMap(agent => {
    // A native child's messageId links its launch, including a result that
    // arrives after the child ended. Its own settings have the last word.
    const launch = agent.launches?.map(id => agents.find(agent => agent.id === `tool:${id}`)).find(agent => agent?.settings)
    const shown = launch ? {...agent, settings: {...launch.settings, ...agent.settings}} : agent
    // A child that sets no effort runs at its own lead's: the session's, or a parent child's.
    const effort = concrete(shown.settings?.effort) ? shown.settings.effort : leadEffort
    return [leadEffort === undefined ? shown : {...shown, leadEffort}, ...engineAgentsOf([...agent.fold.subagents.values()], effort)]
  })
}
const stateText = {working: 'Working', waiting: 'Needs you', completed: 'Finished', failed: 'Failed', stopped: 'Stopped', unknown: 'Unknown'}
const live = (agent: EngineAgent) => agent.state === 'working' || agent.state === 'waiting'

function StopButton({ session, agent }: { session: Session; agent: EngineAgent }) {
  const store = useStore()
  const [stopping, setStopping] = useState(false)
  const pending = useRef(false)
  useEffect(() => { if (!live(agent)) { pending.current = false; setStopping(false) } }, [agent.state])
  if (!live(agent) || !agent.canStop || session.state === 'ended') return null
  const stop = async () => {
    if (pending.current) return
    pending.current = true
    setStopping(true)
    try {
      await store.sendSessionOp({mutation_id: uuid4(), op: 'stop_subagent', args: {session_id: session.id, subagent_id: agent.id}})
    } catch (error) {
      toast(`Could not stop the subagent: ${errorMessage(error)}`, 'error')
    } finally {
      pending.current = false
      setStopping(false)
    }
  }
  return <button type="button" className="button button-quiet button-small code-subagent-stop" aria-label={`Stop ${agent.name}`} disabled={stopping} onClick={() => void stop()}>{stopping ? 'Stopping…' : 'Stop'}</button>
}

export function EngineAgentRow({session, runners, agent, focus, onOpen}: {session: Session; runners: readonly Runner[] | null; agent: ShownEngineAgent; focus: boolean; onOpen: () => void}) {
  const engine = agentLabel(session, runners)
  return <li className={cx('code-subagent', 'is-task', `is-${agent.state}`)}>
    <button type="button" className="code-subagent-open" title={agent.name} autoFocus={focus} onClick={onOpen}>
      <span className={`code-dot is-${live(agent) ? agent.state : agent.state === 'failed' ? 'failed' : 'ended'}`} aria-hidden="true">{agent.state === 'working' && <Working label="Working" aria-hidden="true" />}</span>
      <span className="code-subagent-text">
        <span className="code-subagent-title">{agent.name}</span>{' '}
        <span className="code-subagent-meta"><span className={cx('code-subagent-engine', engine.id && `is-${engine.id}`)}><span className="code-engine-dot" aria-hidden="true" /><span className="code-engine-name">{engine.name}</span></span>{' · '}<SubAgentSettings {...engineSettings(agent.settings, session, runners, agent.leadEffort)} />{' · '}<span className={`code-subagent-status is-${agent.state}`}>{stateText[agent.state]}</span></span>
      </span>
    </button>
    <StopButton session={session} agent={agent} />
  </li>
}

export function EngineAgentView({session, runners, agent, transcript, me, onBack}: {session: Session; runners: readonly Runner[] | null; agent: ShownEngineAgent; transcript: TranscriptView; me: Me | null; onBack: () => void}) {
  return <section className="code-subagent-view" aria-label={agent.name}>
    <header className="code-subagent-head">
      <button type="button" className="button button-small code-subagent-back" aria-label="Back to subagents" autoFocus onClick={onBack}>‹ SubAgents</button>
      <span className="code-subagent-head-actions"><StopButton session={session} agent={agent} /></span>
      <h2 className="code-subagent-name" title={agent.name}>{agent.name}</h2>
      <p className="code-subagent-state"><SubAgentSettings {...engineSettings(agent.settings, session, runners, agent.leadEffort)} /></p>
      <p className="code-subagent-state"><span className={`code-subagent-status is-${agent.state}`}>{stateText[agent.state]}</span></p>
    </header>
    <Transcript view={{...transcript, items: agent.output ? [...agent.fold.items, {id: `${agent.id}/output`, at: agent.at, kind: 'assistant', text: agent.output, streaming: false}] : agent.fold.items, cwd: agent.fold.cwd ?? transcript.cwd}} me={me} busy={live(agent)} />
  </section>
}
